import React from 'react';
import {
  Package,
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  Mail,
  User,
  Plus,
  Check,
  FileText,
  ArrowRight,
  Clock,
  Calendar,
} from 'lucide-react';
import StatusBadge, { OrderTypeBadge } from './ui/StatusBadge';
import ActionedBadge from './ui/ActionedBadge';

export default function DeliveryCard({
  delivery,
  isExpanded,
  onToggle,
  onAddNote,
  onComplete,
}) {
  const isActionable = !delivery.isActioned;
  const lineItems = delivery.lineItems || [delivery];
  const itemCount = lineItems.length;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    // Handle both ISO and simple date formats
    if (timestamp.includes('T')) {
      return timestamp.slice(0, 16).replace('T', ' ');
    }
    return timestamp;
  };

  return (
    <div className="bg-white border border-locate2-border rounded-lg mb-3 overflow-hidden hover:shadow-md transition-shadow">
      <div
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4 flex-1">
          <div className="w-10 h-10 rounded-full bg-locate2-bg-icon flex items-center justify-center">
            <Package size={20} className="text-locate2-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-locate2-dark-blue font-semibold">{delivery.name}</h3>
              <OrderTypeBadge orderType={delivery.orderType} />
              <StatusBadge status={delivery.status} />
              {delivery.isActioned && <ActionedBadge />}
              {itemCount > 1 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-locate2-bg-icon text-locate2-primary">
                  {itemCount} items
                </span>
              )}
            </div>
            <p className="text-locate2-light-gray text-sm mt-1">
              Ref: {delivery.customerReference || '—'} | Customer: {delivery.customerNo}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActionable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onComplete(delivery);
              }}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 bg-status-success text-white rounded-lg text-sm font-medium hover:bg-status-success-hover transition-colors"
            >
              <Check size={16} />
              Mark as Actioned
            </button>
          )}
          <div className="text-right hidden sm:block">
            <p className="text-locate2-medium-gray text-sm font-medium">
              {delivery.suburb}, {delivery.state}
            </p>
            <p className="text-locate2-light-gray text-sm">{delivery.reqDate}</p>
          </div>
          {isExpanded ? (
            <ChevronUp size={20} className="text-locate2-light-gray" />
          ) : (
            <ChevronDown size={20} className="text-locate2-light-gray" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-locate2-border-light">
          {/* Doc Type */}
          {delivery.docType && (
            <div className="flex items-center gap-2 pt-4 pb-1">
              <FileText size={16} className="text-locate2-light-gray" />
              <span className="text-locate2-light-gray text-sm font-medium">Doc Type:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-locate2-bg-icon text-locate2-dark-blue">
                {delivery.docType}
              </span>
            </div>
          )}

          {/* Scheduled Date */}
          {delivery.reqDate && (
            <div className="flex items-center gap-2 pt-4 pb-1">
              <Calendar size={16} className="text-locate2-light-gray" />
              <span className="text-locate2-light-gray text-sm font-medium">Scheduled date:</span>
              <span className="text-locate2-medium-gray text-sm">
                {delivery.reqDate}
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            {/* Contact Details */}
            <div className="space-y-3">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide">
                Contact Details
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <User size={16} className="text-locate2-light-gray" />
                  <span>{delivery.name}</span>
                </div>
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <Phone size={16} className="text-locate2-light-gray" />
                  <span>{delivery.mobile}</span>
                </div>
                <div className="flex items-center gap-2 text-locate2-medium-gray text-sm">
                  <Mail size={16} className="text-locate2-light-gray" />
                  <span className="truncate">{delivery.email}</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="space-y-3">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide">
                Delivery Address
              </h4>
              <div className="flex items-start gap-2 text-locate2-medium-gray text-sm">
                <MapPin size={16} className="text-locate2-light-gray mt-0.5 flex-shrink-0" />
                <span>
                  {delivery.unitNo && `${delivery.unitNo}, `}
                  {delivery.streetNo} {delivery.street}
                  <br />
                  {delivery.suburb}, {delivery.state} {delivery.postCode}
                </span>
              </div>
              {delivery.directionNotes && (
                <p className="text-locate2-light-gray text-sm italic">
                  Note: {delivery.directionNotes}
                </p>
              )}
            </div>
          </div>

          {/* Line Items Table */}
          <div className="mt-4 pt-4 border-t border-locate2-border-light">
            <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide mb-3">
              Order Items ({itemCount})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-locate2-bg-light">
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">SKU</th>
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">Qty</th>
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">Type</th>
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">Install</th>
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">Facility</th>
                    <th className="text-left py-2 px-3 text-locate2-light-gray font-medium">Serial No</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, index) => (
                    <tr key={item.id || index} className="border-b border-locate2-border-light">
                      <td className="py-2 px-3 text-locate2-medium-gray font-medium">{item.sku}</td>
                      <td className="py-2 px-3 text-locate2-medium-gray">{item.qty}</td>
                      <td className="py-2 px-3 text-locate2-medium-gray">{item.deliveryType}</td>
                      <td className="py-2 px-3 text-locate2-medium-gray">{item.installation === 'Y' ? 'Yes' : 'No'}</td>
                      <td className="py-2 px-3 text-locate2-medium-gray">{item.facility}</td>
                      <td className="py-2 px-3 text-locate2-medium-gray">{item.serialNo || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Delivery/Return Notes - collect from all line items */}
          {lineItems.some(item => item.deliveryNotes || item.returnReason) && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              {lineItems.map((item, index) => (
                <div key={item.id || index}>
                  {item.returnReason && (
                    <div className="mb-2">
                      <span className="text-status-error font-medium text-sm">
                        Return Reason ({item.sku}):{' '}
                      </span>
                      <span className="text-locate2-medium-gray text-sm">
                        {item.returnReason}
                      </span>
                    </div>
                  )}
                  {item.deliveryNotes && (
                    <div className="mb-2">
                      <span className="text-locate2-light-gray font-medium text-sm">
                        Delivery Notes ({item.sku}):{' '}
                      </span>
                      <span className="text-locate2-medium-gray text-sm">
                        {item.deliveryNotes}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Status History Section */}
          {delivery.statusHistory && delivery.statusHistory.length > 0 && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide mb-3">
                Status History
              </h4>
              <div className="space-y-2">
                {delivery.statusHistory.map((entry, index) => (
                  <div key={entry.id || index} className="flex items-center gap-2 text-sm">
                    <Clock size={14} className="text-locate2-light-gray flex-shrink-0" />
                    <span className="text-locate2-light-gray min-w-[120px]">
                      {formatTimestamp(entry.changedAt)}
                    </span>
                    {entry.oldStatus ? (
                      <>
                        <StatusBadge status={entry.oldStatus} />
                        <ArrowRight size={14} className="text-locate2-light-gray flex-shrink-0" />
                        <StatusBadge status={entry.newStatus} />
                      </>
                    ) : (
                      <>
                        <span className="text-locate2-light-gray">Created as</span>
                        <StatusBadge status={entry.newStatus} />
                      </>
                    )}
                    {entry.changedBy && entry.changedBy !== 'System' && (
                      <span className="text-locate2-light-gray text-xs">
                        by {entry.changedBy}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Notes Section */}
          {delivery.actionNotes && delivery.actionNotes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light">
              <h4 className="text-locate2-dark-blue font-semibold text-sm uppercase tracking-wide mb-3">
                Action Notes
              </h4>
              <div className="space-y-2">
                {delivery.actionNotes.map((note, index) => (
                  <div key={index} className="bg-locate2-bg-light rounded-lg p-3">
                    <p className="text-locate2-medium-gray text-sm">{note.text}</p>
                    <p className="text-locate2-light-gray text-xs mt-1">
                      {formatTimestamp(note.timestamp)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons for non-completed items */}
          {isActionable && (
            <div className="mt-4 pt-4 border-t border-locate2-border-light flex flex-wrap gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddNote(delivery);
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-transparent border-2 border-locate2-primary text-locate2-primary rounded-lg font-medium hover:bg-locate2-primary hover:text-white transition-colors"
              >
                <Plus size={18} />
                Add Note
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(delivery);
                }}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-status-success text-white rounded-lg font-medium hover:bg-status-success-hover transition-colors"
              >
                <Check size={18} />
                Mark as Actioned
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
